import { createElement } from '../../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../../core/options/options-provider.js';
import { createOptionsCache } from '../../../../core/options/options-cache.js';
import { renderOptionContent } from '../../../../core/options/render-option-content.js';
import { createPortalDropdown } from '../../../../core/dom/portal-dropdown.js';
import { renderChips } from '../../../option-picker/render-chips.js';
import { resolveChipLabelsFromOptions } from '../../../option-picker/resolve-chip-labels.js';

/**
 * Searchable-multi engine for multi-relation fields.
 * Combobox-style multi-select with inline chips, auto-opening dropdown,
 * and toggle behavior (click selected option to deselect).
 *
 * @param {HTMLElement} container
 * @param {import('../../../../contracts/forms.js').FieldDriverContext} ctx
 * @returns {import('../../../../contracts/forms.js').FieldDriverHandle}
 */
export function mount(container, ctx) {
  const { def, onChange } = ctx;
  const mrDef = /** @type {import('../../../../contracts/forms.js').MultiRelationFieldDef} */ (def);
  if (typeof mrDef.loadOptions !== 'function') {
    throw new Error(`multi-relation field '${def.key}' requires loadOptions (or optionsUrl resolved by factory)`);
  }
  const loadOptionsFn = mrDef.loadOptions;

  const debounceMs = mrDef.debounceMs ?? 300;

  let destroyed = false;
  let forcedDisabled = false;
  /** @type {string[]} */
  let currentValues = Array.isArray(ctx.value) ? [.../** @type {string[]} */ (ctx.value)] : [];
  /** @type {import('../../../../contracts/forms.js').FieldOption[]} */
  let currentOptions = [];
  /** @type {boolean} */
  let isOpen = false;
  /** @type {number} */
  let highlightedIndex = -1;

  // --- DOM ---
  const wrapper = createElement('div', { class: 'searchable-multi' });

  const control = createElement('div', { class: 'searchable-multi__control' });
  wrapper.appendChild(control);

  const chipsContainer = createElement('span', { class: 'searchable-multi__chips' });
  control.appendChild(chipsContainer);

  const panelId = `searchable-multi-panel-${def.key}`;

  const input = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text',
    class: 'searchable-multi__input',
    id: `field-${def.key}`,
    role: 'combobox',
    'aria-expanded': 'false',
    'aria-haspopup': 'listbox',
    'aria-controls': panelId,
    autocomplete: 'off',
    placeholder: def.placeholder ?? 'Search...',
  }));
  if (def.required) input.setAttribute('aria-required', 'true');
  if (def.disabled) {
    input.disabled = true;
    wrapper.setAttribute('aria-disabled', 'true');
  }
  control.appendChild(input);

  // Clear button — visible only when input has a non-empty search query.
  // Without this, after typing 'foo' there is no way to revert to the
  // unfiltered list short of selecting/deselecting an option.
  const clearBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button',
    class: 'searchable-multi__clear',
    'aria-label': 'Clear search',
    tabindex: '-1',
    hidden: 'hidden',
  }));
  clearBtn.textContent = '×';
  control.appendChild(clearBtn);

  function updateClearButtonVisibility() {
    if (input.value === '') clearBtn.setAttribute('hidden', 'hidden');
    else clearBtn.removeAttribute('hidden');
  }

  /**
   * Resets the search query to '' and refocuses the input. Used by:
   *  - explicit clear button click
   *  - toggleOption (after a selection commits, the user is starting a fresh search)
   *  - chip removal (selection set changed, search context becomes stale)
   *  - Escape (progressive: first Esc clears query if non-empty, second closes)
   */
  function clearSearch() {
    if (input.value === '') return;
    input.value = '';
    updateClearButtonVisibility();
    provider.load('', ctx.getFormValues?.() ?? {});
    input.focus();
  }

  // Dropdown panel — body-appended on open() by portal helper.
  const panel = createElement('div', {
    class: 'searchable-multi__dropdown',
  });
  panel.style.display = 'none';

  const optionsList = createElement('div', {
    class: 'searchable-multi__options',
    role: 'listbox',
    id: panelId,
  });
  panel.appendChild(optionsList);

  // Panel is body-appended on portal.open() (against anchor.ownerDocument),
  // not at construction. Tests that check `document.body.querySelector(...)`
  // for the dropdown must trigger open() first.

  container.appendChild(wrapper);

  // --- Options provider (with TTL cache) ---
  const cache = createOptionsCache();
  const cacheNs = `multi-relation:${def.key}`;

  /** @type {import('../../../../contracts/forms.js').OptionLoader} */
  const cachedLoadOptions = async (opts) => {
    const cacheKey = JSON.stringify({ q: opts.query, v: opts.values ?? {} });
    if (cache.has(cacheNs, cacheKey)) {
      return /** @type {any} */ (cache.get(cacheNs, cacheKey));
    }
    const result = await loadOptionsFn(opts);
    cache.set(cacheNs, cacheKey, result);
    return result;
  };

  const provider = createOptionsProvider({
    loadOptions: cachedLoadOptions,
    mapOption: mrDef.mapOption,
    debounceMs,
  });

  // --- Portal lifecycle (panel positioning, outside-click, scroll/resize) ---
  const portal = createPortalDropdown({
    anchor: wrapper,
    panel,
    onClose: () => closeDropdown(),
  });

  // --- Chip rendering ---
  /** @type {Map<string, string>} */
  const chipLabels = new Map();

  function doRenderChips() {
    renderChips(chipsContainer, {
      values: currentValues,
      labels: chipLabels,
      classes: { chip: 'searchable-multi__chip', removeBtn: 'searchable-multi__chip-remove' },
      onRemove: (id) => removeValue(id),
      disabled: forcedDisabled || Boolean(def.disabled),
    });
  }

  /** @param {string} id */
  function removeValue(id) {
    const idx = currentValues.indexOf(id);
    if (idx === -1) return;
    currentValues = [...currentValues.slice(0, idx), ...currentValues.slice(idx + 1)];
    chipLabels.delete(id);
    doRenderChips();
    // Clear any active search — selection set changed, leaving a stale query
    // on the dropdown is confusing UX. Idempotent if input is already empty.
    clearSearch();
    if (isOpen) renderOptions();
    onChange(currentValues);
  }

  function applyDisabled() {
    const shouldDisable = forcedDisabled || Boolean(def.disabled) || provider.getState().loading;
    input.disabled = shouldDisable;
    clearBtn.disabled = shouldDisable;
    wrapper.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
    doRenderChips();
  }

  // Resolve labels for initial values
  for (const id of currentValues) {
    chipLabels.set(id, `#${id}`);
    if (mrDef.resolveLabel) {
      mrDef.resolveLabel(id).then((label) => {
        if (destroyed) return;
        chipLabels.set(id, label);
        doRenderChips();
      }).catch(() => { /* keep #id placeholder */ });
    }
  }

  const unsubscribe = subscribeWithInitial(provider, (state) => {
    if (destroyed) return;
    currentOptions = state.options.slice();
    applyDisabled();
    const { changed } = resolveChipLabelsFromOptions({
      values: currentValues,
      labels: chipLabels,
      options: currentOptions,
    });
    if (changed) doRenderChips();
    if (isOpen) {
      renderOptions();
    }
  });

  provider.load('', ctx.getFormValues?.() ?? {});

  function renderOptions() {
    optionsList.textContent = '';
    highlightedIndex = -1;
    currentOptions.forEach((opt, idx) => {
      const isSelected = currentValues.includes(opt.value);
      const optEl = createElement('div', {
        role: 'option',
        class: 'searchable-multi__option' + (isSelected ? ' searchable-multi__option--selected' : ''),
        'aria-selected': isSelected ? 'true' : 'false',
        id: `searchable-multi-${def.key}-opt-${idx}`,
      });
      renderOptionContent(opt, optEl);
      optEl.addEventListener('mousedown', (e) => {
        e.preventDefault();
        // toggleOption rebuilds optionsList, detaching the mousedown target.
        // Without this guard, the portal's outside-mousedown handler would see
        // the detached node as "outside" and close the dropdown.
        portal.suppressNextOutsideClose();
        toggleOption(opt);
      });
      optEl.addEventListener('mouseover', () => {
        setHighlight(idx);
      });
      optionsList.appendChild(optEl);
    });
  }

  /** @param {number} idx */
  function setHighlight(idx) {
    const items = optionsList.querySelectorAll('.searchable-multi__option');
    items.forEach((item, i) => {
      item.classList.toggle('searchable-multi__option--highlighted', i === idx);
    });
    highlightedIndex = idx;
    if (idx >= 0 && items[idx]) {
      input.setAttribute('aria-activedescendant', items[idx].id || '');
    }
  }

  /** @param {import('../../../../contracts/forms.js').FieldOption} opt */
  function toggleOption(opt) {
    if (currentValues.includes(opt.value)) {
      // Deselect — remove (also clears search via removeValue → clearSearch).
      removeValue(opt.value);
    } else {
      // Select — add
      currentValues = [...currentValues, opt.value];
      chipLabels.set(opt.value, opt.label);
      doRenderChips();
      // Clear the search after every selection. Dropdown stays open
      // (multi-select UX); fresh empty query gives the user the unfiltered
      // option list ready for the next selection.
      clearSearch();
      renderOptions();
      onChange(currentValues);
    }
    // Keep dropdown open, refocus input
    input.focus();
  }

  function openDropdown() {
    if (isOpen || input.disabled) return;
    isOpen = true;
    input.setAttribute('aria-expanded', 'true');
    portal.open();
    provider.load(input.value, ctx.getFormValues?.() ?? {});
    renderOptions();
  }

  function closeDropdown() {
    if (!isOpen) return;
    isOpen = false;
    input.setAttribute('aria-expanded', 'false');
    portal.close();
    input.removeAttribute('aria-activedescendant');
  }

  // --- Events ---
  input.addEventListener('focus', () => {
    openDropdown();
  });

  input.addEventListener('input', () => {
    if (!isOpen) openDropdown();
    provider.load(input.value, ctx.getFormValues?.() ?? {});
    updateClearButtonVisibility();
  });

  // mousedown + preventDefault keeps focus on the input through the click —
  // a bare click listener would let the browser blur the input first.
  clearBtn.addEventListener('mousedown', (e) => { e.preventDefault(); });
  clearBtn.addEventListener('click', () => {
    // Defensive: applyDisabled() already sets clearBtn.disabled, so the click
    // shouldn't fire on a disabled button — but a stray programmatic dispatch
    // or assistive-tech action could still reach here. Keep state immutable
    // when the field is disabled.
    if (clearBtn.disabled) return;
    clearSearch();
  });

  // Refocus on control click — but skip when the click target is the input
  // itself or the clear button. Re-calling input.focus() during a typing
  // burst is a no-op in most browsers but can flicker focus state on Chrome
  // and risks interrupting IME composition.
  control.addEventListener('click', (e) => {
    if (input.disabled) return;
    if (e.target === input || e.target === clearBtn) return;
    input.focus();
  });

  /**
   * @param {KeyboardEvent} e
   */
  function handleKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setHighlight(Math.min(highlightedIndex + 1, currentOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setHighlight(Math.max(highlightedIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      } else if (highlightedIndex >= 0 && currentOptions[highlightedIndex]) {
        toggleOption(currentOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Progressive escape: first Esc clears a non-empty search; second Esc
      // (or first if already empty) closes the dropdown.
      if (input.value !== '') {
        clearSearch();
      } else {
        closeDropdown();
      }
    }
  }

  input.addEventListener('keydown', handleKeydown);

  // Render initial chips
  doRenderChips();

  return {
    setValue(value) {
      currentValues = Array.isArray(value) ? [.../** @type {string[]} */ (value)] : [];
      chipLabels.clear();
      for (const id of currentValues) {
        const opt = currentOptions.find(o => o.value === id);
        if (opt) {
          chipLabels.set(id, opt.label);
        } else {
          chipLabels.set(id, `#${id}`);
          if (mrDef.resolveLabel) {
            mrDef.resolveLabel(id).then((label) => {
              if (destroyed) return;
              chipLabels.set(id, label);
              doRenderChips();
            }).catch(() => {});
          }
        }
      }
      doRenderChips();
      if (isOpen) renderOptions();
    },
    getValue() {
      return currentValues;
    },
    setErrors() {
      // errors rendered by host
    },
    destroy() {
      destroyed = true;
      unsubscribe();
      provider.destroy();
      cache.destroy();
      closeDropdown();
      portal.destroy();
      wrapper.remove();
    },
    setDisabled(disabled) {
      forcedDisabled = disabled;
      applyDisabled();
    },
    setVisible(visible) {
      if (!visible && isOpen) closeDropdown();
    },
    clearValue() {
      // "Clear" means neutral UI: empty selection AND empty search. Without
      // the latter, host-driven Reset (e.g. crud-editor close+reopen) would
      // leave a stale query in the input + filtered dropdown.
      const hadValues = currentValues.length > 0;
      const hadQuery = input.value !== '';
      if (!hadValues && !hadQuery) return;
      currentValues = [];
      chipLabels.clear();
      doRenderChips();
      if (hadQuery) {
        // clearSearch() refocuses the input, which would re-open the dropdown
        // via the focus listener. Avoid that side effect by resetting input
        // state directly here (silent reset semantics).
        input.value = '';
        updateClearButtonVisibility();
        provider.load('', ctx.getFormValues?.() ?? {});
      }
      if (isOpen) renderOptions();
      // No onChange — silent host-driven reset
    },
    getOptionsProvider() {
      return provider;
    },
    setAriaInvalid(invalid) {
      wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    },
  };
}
